import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { formatInclusionPrv } from './inclusionsModel';
import type { DealOptionInclusionGroup } from './inclusionsModel';

interface Props {
  currency: string;
  groups: Array<DealOptionInclusionGroup>;
  optionLabel: string;
}

export default function DealOptionInclusionsView({ currency, groups, optionLabel }: Props) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" color="text.secondary">
        Showing inclusions for <strong>{optionLabel}</strong>. Expand a room type to see all deal options. Changes in{' '}
        <strong>Edit</strong> update this list.
      </Typography>

      {groups.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No package options yet - add rooms and lengths of stay in Package Options first.
        </Typography>
      ) : (
        groups.map((group, groupIndex) => (
          <Accordion key={group.id} defaultExpanded={groupIndex === 0} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 1 }}>
                <Typography variant="subtitle2">
                  {group.roomName} ({group.deals.length})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {group.deals.length} deal option{group.deals.length === 1 ? '' : 's'}
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {group.deals.map((dealRow, dealIndex) => {
                  const rows = [
                    ...dealRow.inclusions.map((inclusion) => ({
                      key: inclusion.id,
                      name: inclusion.name ?? 'Untitled inclusion',
                      // See docs/business-rules.md#salesforce-data-model.
                      prv: '—',
                      currency: inclusion.currencyisocode ?? currency,
                      finePrint: inclusion.inclusion_description__c,
                      pending: false,
                    })),
                    ...dealRow.draftInclusions.map((draft) => ({
                      key: draft.id,
                      name: draft.name,
                      prv: formatInclusionPrv(draft.prv),
                      currency,
                      finePrint: draft.description,
                      pending: true,
                    })),
                  ];

                  return (
                    <Accordion key={dealRow.id} defaultExpanded={groupIndex === 0 && dealIndex === 0} disableGutters>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ width: '100%', pr: 1 }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">{dealRow.name}</Typography>
                            {dealRow.nightCount !== undefined && <Chip label={`${dealRow.nightCount}N`} size="small" />}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {rows.length} inclusion{rows.length === 1 ? '' : 's'}
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        {rows.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            No inclusions attached to this deal option.
                          </Typography>
                        ) : (
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Inclusion</TableCell>
                                  <TableCell align="right">PRV</TableCell>
                                  <TableCell>Currency</TableCell>
                                  <TableCell>Fine print</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {rows.map((row) => (
                                  <TableRow key={row.key}>
                                    <TableCell>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <span>{row.name}</span>
                                        {row.pending && (
                                          <Chip label="Pending sync" size="small" color="warning" variant="outlined" />
                                        )}
                                      </Stack>
                                    </TableCell>
                                    <TableCell align="right">{row.prv}</TableCell>
                                    <TableCell>{row.currency}</TableCell>
                                    <TableCell>{row.finePrint || '—'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Stack>
  );
}
